import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import appLogo from "@/icon.png";
import { parseDeviceIdAndAccessCodeFromLink } from "@/utils/remote";

/**
 * 连接设备面板，提供链接接入与设备 ID 输入两种方式
 * @param {object} props 组件参数
 * @param {(deviceId: string, passCode: string) => void} props.onSubmit 提交表单/链接
 * @param {string} [props.defaultDeviceId]
 * @param {string} [props.defaultPassCode]
 * @param {string} [props.defaultLinkValue]
 * @param {boolean} props.isLoading 是否展示加载状态
 * @param {boolean} props.showError 是否展示错误提示
 * @param {string} [props.errorMessage] 错误提示文案（UI 使用英文）
 */

type ConnectFormValues = {
	device_id: string;
	pass_code: string;
};

type ConnectDeviceCardProps = {
	onSubmit: (deviceId: string, passCode: string) => void;
	defaultDeviceId?: string;
	defaultPassCode?: string;
	defaultLinkValue?: string;
	isLoading: boolean;
	showError: boolean;
	errorMessage?: string;
};

const ConnectDeviceCard = ({
	onSubmit,
	defaultDeviceId = "",
	defaultPassCode = "",
	defaultLinkValue = "",
	isLoading,
	showError,
	errorMessage = "",
}: ConnectDeviceCardProps) => {
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<ConnectFormValues>({
		defaultValues: {
			device_id: defaultDeviceId,
			pass_code: defaultPassCode,
		},
	});

	const [linkValue, setLinkValue] = useState(defaultLinkValue || "");
	const [isLinkMode, setIsLinkMode] = useState(
		(defaultLinkValue || "").length > 0,
	);
	const [showPassword, setShowPassword] = useState(false);
	const handleCredentialsSubmit = (data: ConnectFormValues) => {
		onSubmit(data.device_id, data.pass_code);
	};

	/**
	 * 从邀请链接解析设备信息并发起连接
	 */
	const connectUsingLink = () => {
		if (!linkValue.trim()) {
			toast.error("Please provide a valid invitation link");
			return;
		}

		try {
			const parsed = parseDeviceIdAndAccessCodeFromLink(linkValue) as {
				d: string;
				a: string;
			};
			onSubmit(parsed.d, parsed.a);
		} catch (error) {
			console.error("Error decoding base64 parameters:", error);

			const message =
				error instanceof Error
					? error.message
					: "Invalid invitation link format";
			toast.error(message);
		}
	};

	return (
		<div className="rounded-3xl bg-white/90 p-6 shadow-2xl backdrop-blur sm:p-10">
			<div className="flex flex-col items-center text-center">
				<img src={appLogo} alt="EasyRemote logo" className="h-20" />
				<h2 className="mt-4 text-xl font-semibold text-slate-900">
					Connect Device
				</h2>
				<p className="mt-2 text-sm text-slate-500">
					Use a shared link or device ID to verify and join the signal server.
				</p>
			</div>

			<div className="mt-8">
				<div className="flex justify-center gap-2 text-sm">
					<button
						type="button"
						className={`btn btn-sm ${isLinkMode ? "btn-primary" : "btn-ghost"}`}
						onClick={() => {
							setIsLinkMode(true);
						}}
					>
						Connect Via Link
					</button>
					<button
						type="button"
						className={`btn btn-sm ${!isLinkMode ? "btn-primary" : "btn-ghost"}`}
						onClick={() => {
							setIsLinkMode(false);
						}}
					>
						Connect Via Device ID
					</button>
				</div>

				{isLinkMode && (
					<div className="mt-6 space-y-4">
						<label
							className="block text-sm font-medium text-slate-600"
							htmlFor="connect-link"
						>
							Invitation Link
						</label>
						<div className="flex flex-col gap-3 sm:flex-row">
							<input
								id="connect-link"
								className="input input-bordered flex-1"
								type="url"
								placeholder="Paste invitation link"
								name="connect-link"
								autoComplete="off"
								spellCheck={false}
								value={linkValue}
								onChange={(e) => setLinkValue(e.target.value.trim())}
							/>
							<button
								className="btn btn-primary sm:w-40"
								type="button"
								onClick={() => {
									connectUsingLink();
								}}
								disabled={isLoading}
							>
								{isLoading ? (
									<span className="loading loading-spinner"></span>
								) : (
									"Connect"
								)}
							</button>
						</div>
					</div>
				)}

				{!isLinkMode && (
					<form
						onSubmit={handleSubmit(handleCredentialsSubmit)}
						autoComplete="off"
						className="mt-6 space-y-5"
					>
						<div className="space-y-2">
							<label
								className="block text-sm font-medium text-slate-600"
								htmlFor="device_id"
							>
								Device ID
							</label>
							<input
								id="device_id"
								type="text"
								className="input input-bordered w-full"
								placeholder="E00123"
								autoComplete="off"
								spellCheck={false}
								aria-invalid={Boolean(errors.device_id)}
								aria-describedby={
									errors.device_id ? "device_id-error" : undefined
								}
								{...register("device_id", { required: true })}
							/>
							{errors.device_id && (
								<span
									id="device_id-error"
									className="text-xs text-red-500"
									role="alert"
								>
									Device ID is required
								</span>
							)}
						</div>

						<div className="space-y-2">
							<label
								className="block text-sm font-medium text-slate-600"
								htmlFor="pass_code"
							>
								Access Code
							</label>
							<div className="relative">
								<input
									id="pass_code"
									type={showPassword ? "text" : "password"}
									{...register("pass_code", { required: true })}
									placeholder="******"
									className="input input-bordered w-full pr-12"
									autoComplete="one-time-code"
									spellCheck={false}
									aria-invalid={Boolean(errors.pass_code)}
									aria-describedby={
										errors.pass_code ? "pass_code-error" : undefined
									}
								/>
								<button
									type="button"
									className="btn btn-ghost btn-xs absolute right-2 top-2.5"
									onClick={() => {
										setShowPassword(!showPassword);
									}}
								>
									{showPassword ? "Hide" : "Show"}
								</button>
							</div>
							{errors.pass_code && (
								<span
									id="pass_code-error"
									className="text-xs text-red-500"
									role="alert"
								>
									Access code is required
								</span>
							)}
						</div>

						<button
							className="btn btn-primary w-full"
							type="submit"
							disabled={isLoading}
						>
							{isLoading ? (
								<span className="loading loading-spinner"></span>
							) : (
								"Connect Device"
							)}
						</button>
					</form>
				)}
			</div>

			{isLoading && (
				<div className="mt-6 flex items-center justify-center gap-3 text-sm text-slate-500">
					<span className="loading loading-spinner"></span>
					<span>Connecting to signal server...</span>
				</div>
			)}

			{showError && (
				<p className="mt-6 text-center text-sm text-red-500">
					{errorMessage ||
						"Connection failed. Please verify the information and try again."}
				</p>
			)}
		</div>
	);
};

export default ConnectDeviceCard;
